<template>
  <CustomDropdown
    ref="sequenceDropdown"
    maxHeight="300px"
    gap="10px"
    position="start"
    dropdownCSS="p-0"
    :disabled="disabled"
    @close="resetSearch"
  >
    <template #trigger>
      <div class="secondary_small gap-1">
        <ThemeImage
          class="size-4"
          :icon="imageNames.ADD_TO_SEQUENCE_GREY"
          alt="add_to_sequence"
        />
        Add to {{ featureNames.SEQUENCES }}
        <ThemeImage
          class="size-4"
          :icon="imageNames.DROPDOWN_GREY"
          alt="dropdown_icon"
        />
      </div>
    </template>

    <template #default>
      <div class="flex w-[266px] flex-col">
        <div class="flex flex-col max-h-[300px]">
          <div
            class="flex flex-col py-3 px-2"
            :class="{ border_bottom: sequences?.length }"
          >
            <span
              class="secondary_color font-semibold text-xs leading-[17px] px-3 pb-2"
            >
              CHOOSE A {{ featureNames.SEQUENCES.toUpperCase() }}
            </span>
            <CustomInput
              format="text"
              v-model="query"
              placeholder="Search here"
              type="primary"
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search_icon"
              auto-focus
            />
          </div>

          <div
            v-if="sequences?.length"
            class="flex flex-col p-2 overflow-y-scroll custom_scrollbar"
          >
            <a-tooltip
              v-for="(sequence, index) in sequences"
              :key="`${sequence?.id}_sequence_${index}`"
              placement="top"
              :title="
                shouldShowTooltip(sequence.id + '_' + index)
                  ? sequence?.name || ''
                  : ''
              "
            >
              <div
                class="dropdown_item flex-shrink-0 flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
                @click="handleSequenceSelected(sequence.id)"
              >
                <div
                  class="w-full flex flex-row items-center justify-between gap-1 text-sm"
                >
                  <div class="flex flex-col w-[65%] gap-1">
                    <div
                      class="truncate"
                      :ref="
                        (el) => setContainerRef(el, sequence.id + '_' + index)
                      "
                    >
                      <span
                        :ref="(el) => setTextRef(el, sequence.id + '_' + index)"
                        class="text_color truncate text-sm font-normal leading-[17px]"
                        >{{ sequence?.name || "" }}</span
                      >
                    </div>
                  </div>
                  <div
                    :class="[
                      'px-2 flex items-center  justify-center py-0 rounded-full',
                      sequence?.status === 'Active'
                        ? 'active_sequence_tag'
                        : 'inactive_sequence_tag',
                    ]"
                  >
                    <span
                      :class="[
                        sequence?.status === 'Active'
                          ? 'text-white'
                          : 'text_color',
                        'text-[11px] font-semibold',
                      ]"
                    >
                      {{
                        sequence?.status === "Active" ? "Active" : "Inactive"
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </a-tooltip>
          </div>

          <div class="flex flex-col p-2 border_top">
            <div
              class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
              @click="handleCreateSequenceClick"
            >
              + Create New {{ featureNames.SEQUENCES }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { ref, watch } from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import SequenceService from "@/src/api/SequenceService.js";
import { useTruncationTooltip } from "@/src/composables/useTruncationTooltip.js";
import { debounce } from "lodash";
import { imageNames } from "@/src/constants/imageConstants.js";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["sequence-selected", "create-sequence"]);

const { setTextRef, setContainerRef, shouldShowTooltip } =
  useTruncationTooltip();

const sequenceDropdown = ref(null);
const sequences = ref([]);
const query = ref("");

const fetchSequences = async (searchQuery = "") => {
  try {
    const response = await SequenceService.fetchSequences(0, 20, searchQuery);

    if (response?.success) {
      sequences.value = response?.sequences || [];
    } else {
      console.error("Failed to fetch sequences:", response.errors);
    }
  } catch (error) {
    console.error("Error fetching sequences:", error);
  }
};

const debouncedFetchSequences = debounce(fetchSequences, 300);

const resetSearch = () => {
  query.value = "";
};

const handleSequenceSelected = (sequenceId) => {
  try {
    sequenceDropdown.value.closeDropdown();
    emit("sequence-selected", { sequenceId });

    resetSearch();
  } catch (error) {
    console.error("Error handling sequence selection:", error);
  }
};

const handleCreateSequenceClick = () => {
  emit("create-sequence");
  resetSearch();
};

watch(
  query,
  (newValue) => {
    if (props.disabled) return;
    debouncedFetchSequences(newValue);
  },
  {
    immediate: true,
  },
);

watch(
  () => props.disabled,
  (isDisabled) => {
    if (!isDisabled) debouncedFetchSequences(query.value);
  },
);
</script>
